import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../../instrumentation/instrumentation.service';
import { ActionContextMenu } from '../../models/action-context-menu.model';
import { ContextMenuItem } from '../../models/context-menu-item.model';
import { Menu } from '../../models/menu.model';
import { ActionContextMenuService } from '../../services/action-context-menu.service';
import { DeviceDetector } from '../../services/device-detector.service';
import { CapabilitiesService } from '../../shared-context/capabilities.service';
import { LocService } from '../../shared-context/loc.service';
import { DropdownMenu, DropdownMenuComponent } from '../dropdown-menu/dropdown-menu.component';
import { MenuSelectItemEvent } from '../menu/menu-select-item.model';

@Component({
    selector: 'ohp-actions-menu',
    templateUrl: './actions-menu.component.html'
})
export class ActionsMenuComponent implements OnChanges, OnInit {

    @Input() public menuModel: Menu<ContextMenuItem>;

    @Input() public listItemIndex: number;

    @Input() public iconClass: string;

    @Input() public area: string;

    @Input() public dataSecurity: DataSecurityLevel;

    @Input() public leftAlignedCard?: boolean = false;

    @Input() public documentName: string;

    @Input() public horizontalEllipsis?: boolean = false;

    @Input() public actionContextMenuEnabled?: boolean = false;

    @Input() public actionContextMenu?: ActionContextMenu;

    @Output() public readonly menuToggled: EventEmitter<boolean>;

    @Output() public readonly menuFocused: EventEmitter<boolean>;

    public mobileMenusEnabled: boolean;

    public dropdownMenuModel: DropdownMenu<ContextMenuItem>;

    public fluentRefresh: boolean;

    public isOpen: boolean;

    @ViewChild(DropdownMenuComponent) public dropdownMenu: DropdownMenuComponent;

    constructor(
        private readonly actionContextMenuService: ActionContextMenuService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly deviceDetector: DeviceDetector,
        private readonly instrumentationService: InstrumentationService,
        private readonly locService: LocService
    ) {
        this.menuToggled = new EventEmitter<boolean>();
        this.menuFocused = new EventEmitter<boolean>();
        this.mobileMenusEnabled = this.capabilitiesService.capabilities.mobileContextMenusEnabled;
    }

    public ngOnChanges(changes: SimpleChanges) {
        // If menuModel changes, we want to propagate the change to dropdownMenuModel as well
        if (changes.hasOwnProperty('menuModel')) {
            this.initializeDropdownMenuModel(this.menuModel);
        }
    }

    public ngOnInit() {
        this.fluentRefresh = this.capabilitiesService.capabilities.fluentRefresh || this.horizontalEllipsis;
        this.initializeDropdownMenuModel(this.menuModel);
    }

    public onFocusLink(focused: boolean) {
        this.menuFocused.emit(focused);
    }

    public onToggleMenu(open: boolean): void {
        if (this.actionContextMenuEnabled) {
            const rect = this.dropdownMenu.dropdownLink.nativeElement.getBoundingClientRect();
            this.actionContextMenu.targetElement = {
                current: {
                    left: rect.left,
                    top: rect.top + rect.height
                }
            };
            this.actionContextMenuService.openMenu(this.actionContextMenu);
            return;
        }

        if (open) {
            this.menuModel.rightAligned = true;
        }
        this.isOpen = open;
        this.menuToggled.emit(open);
        this.instrumentToggle(open);
    }

    public onSelectItem(event: MenuSelectItemEvent<ContextMenuItem>) {
        event.item.executeAction(event.nativeDomEvent);
    }

    private initializeDropdownMenuModel(menu: Menu<ContextMenuItem>) {
        this.dropdownMenuModel = menu as DropdownMenu<ContextMenuItem>;
        if (!this.dropdownMenuModel) {
            throw new Error('menuModel must be provided to actions-menu');
        }

        this.dropdownMenuModel.linkAriaLabel = this.locService.format(this.locService.MruActionsMenuAriaLabel, this.documentName);
        this.dropdownMenuModel.linkTooltip = this.locService.MruActionsMenuToolTip;
        this.dropdownMenuModel.linkCssClass = `actions-menu-link ${this.fluentRefresh ? 'commanding-item--fluent ms-fcl-npa ms-fcl-tp-h ms-fcl-tda-a' : 'ms-fcl-nd ms-bgc-g40-f ms-bgc-g40-h ms-bgc-g40-a commanding-item ms-bgc-g40-h'} ${this.deviceDetector.isDesktopDevice() ? ' actions-menu-link--hidden' : ''}`;
    }

    private instrumentToggle(opened: boolean) {
        const action = opened ? 'Open' : 'Close';
        const params: InstrumentationParams = {};
        params[InstrumentationParam.RenderSize] = this.menuModel.items.length.toString();
        params[InstrumentationParam.Index] = this.listItemIndex.toString();
        params[InstrumentationParam.Action] = action;

        this.instrumentationService.logClickEvent(KnownInstrumentationEvents.ActionsMenuToggle, 'actions-menu-link', this.area, null, null, params, [], this.dataSecurity || DataSecurityLevel.Security);
    }
}
